import React, { useEffect, useState } from 'react';
import { View, Text } from "../../Components/Themed";
import { ScrollView, TouchableOpacity } from 'react-native';
import { useColors } from '../../constants/useColors';
import { Icons, SearchBox, Button } from '../../Components';
import { getLeagues, BetRouterApi } from '../api';
import type { RouterLeagueProps } from '../types';
import LeagueInfo from '../components/admin/LeagueInfo';
import LeagueContests from '../components/admin/LeagueContests';
import LeagueParticipants from '../components/admin/LeagueParticipants';
import AutoFillView from '../components/admin/AutoFillView';
import ConfigManager from '../components/admin/ConfigManager';
import JobManager from '../components/admin/JobManager';
import RouterMarkets from '../components/admin/RouterMarkets';

type NavigationSection = 'info' | 'contests' | 'participants';
type GlobalTool = 'jobs' | 'auto-link' | 'auto-fill' | 'config' | 'router-markets';

type DesktopAdminLayoutProps = {
    insets: { top: number; bottom: number; left: number; right: number };
    onClose?: () => void;
    initialContestId?: string; // Optional contest ID to deep link to
    initialLeagueId?: string; // Optional league ID to deep link to
};

const DesktopAdminLayout = ({ insets, onClose, initialContestId, initialLeagueId }: DesktopAdminLayoutProps) => {
    const Colors = useColors();

    // Navigation state
    const [leagues, setLeagues] = useState<RouterLeagueProps[]>([]);
    const [expandedLeagueId, setExpandedLeagueId] = useState<string | null>(null);
    const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);
    const [selectedSection, setSelectedSection] = useState<NavigationSection>('info');
    const [selectedTool, setSelectedTool] = useState<GlobalTool | null>(null);
    const [searchText, setSearchText] = useState('');
    const [reconcilingOpportunities, setReconcilingOpportunities] = useState(false);

    // Load leagues on mount
    useEffect(() => {
        BetRouterApi.setEnvironment();
        loadLeagues();
    }, []);

    // Handle deep linking to specific contest
    useEffect(() => {
        if (initialLeagueId && leagues.length > 0) {
            console.log('Deep linking to league:', initialLeagueId);
            // Select and expand the league
            setSelectedLeagueId(initialLeagueId);
            setExpandedLeagueId(initialLeagueId);
            setSelectedSection('contests');
            setSelectedTool(null);
        }
    }, [initialLeagueId, leagues]);

    const loadLeagues = async () => {
        try {
            const response = await getLeagues('active');
            const leaguesData = Array.isArray(response) ? response : [];

            setLeagues(leaguesData);
            // Auto-select first league if available (only if not deep linking)
            if (leaguesData.length > 0 && !selectedLeagueId && !initialLeagueId) {
                const firstLeague = leaguesData[0];
                if(!firstLeague){ return }
                setSelectedLeagueId(firstLeague.router_league_id);
                setExpandedLeagueId(firstLeague.router_league_id);
            }
        } catch (error) {
            console.error('Failed to load leagues:', error);
        }
    };

    const toggleLeagueExpansion = (leagueId: string) => {
        if (expandedLeagueId === leagueId) {
            setExpandedLeagueId(null);
        } else {
            setExpandedLeagueId(leagueId);
            setSelectedLeagueId(leagueId);
            setSelectedSection('info');
            setSelectedTool(null); // Clear any selected tool
        }
    };

    const selectSection = (leagueId: string, section: NavigationSection) => {
        setSelectedLeagueId(leagueId);
        setSelectedSection(section);
        setSelectedTool(null);
    };

    const selectTool = (tool: GlobalTool) => {
        setSelectedTool(tool);
        setSelectedSection('info'); // Reset section when selecting a tool
    };

    const handleReconcileOpportunities = async () => {
        setReconcilingOpportunities(true);
        try {
            await BetRouterApi.BetRouter.OpportunityApi.reconcileOpportunities();
            alert('Opportunities reconciled successfully!');
        } catch (error) {
            console.error('Failed to reconcile opportunities:', error);
            alert('Failed to reconcile opportunities. Check console for details.');
        } finally {
            setReconcilingOpportunities(false);
        }
    };

    const filteredLeagues = leagues.filter(league =>
        league.name.toLowerCase().includes(searchText.toLowerCase())
    );

    const selectedLeague = leagues.find(l => l.router_league_id === selectedLeagueId);

    const renderLeagueNav = (league: RouterLeagueProps) => {
        const isExpanded = expandedLeagueId === league.router_league_id;
        const isSelected = selectedLeagueId === league.router_league_id;

        return (
            <View key={league.router_league_id} transparent style={{ marginBottom: 4 }}>
                {/* League Header */}
                <TouchableOpacity
                    onPress={() => toggleLeagueExpansion(league.router_league_id)}
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        backgroundColor: isSelected ? Colors.views.body : 'transparent',
                        borderRadius: 6,
                    }}
                >
                    <Icons.ChevronIcon
                        direction={isExpanded ? 'down' : 'right'}
                        size={8}
                        color={Colors.text.h1}
                    />
                    <Text theme='h2' size={14} style={{ flex: 1, marginLeft:10 }}>
                        {league.name}
                    </Text>
                </TouchableOpacity>

                {/* Sub-sections */}
                {isExpanded && (
                    <View transparent style={{ marginLeft: 24, marginTop: 4 }}>
                        {(['info', 'contests', 'participants'] as NavigationSection[]).map(section => {
                            const isActive = isSelected && selectedSection === section && !selectedTool;
                            return (
                                <TouchableOpacity
                                    key={section}
                                    onPress={() => selectSection(league.router_league_id, section)}
                                    style={{
                                        padding: 10,
                                        paddingLeft: 16,
                                        backgroundColor: isActive ? Colors.views.header : 'transparent',
                                        borderRadius: 4,
                                        marginBottom: 2,
                                    }}
                                >
                                    <Text
                                        theme={isActive ? 'h2' : 'description'}
                                        size={13}
                                    >
                                        {section.charAt(0).toUpperCase() + section.slice(1)}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                )}
            </View>
        );
    };

    return (
        <View transparent style={{ flex: 1, flexDirection: 'row' }}>
            {/* Left Navigation Sidebar */}
            <View
                type='body'
                style={{
                    width: 280,
                    borderRightWidth: 2,
                    borderRightColor: Colors.borders.light,
                }}
            >
                {/* Header with close button */}
                <View
                    type='header'
                    style={{
                        padding: 16,
                        paddingTop: insets.top + 16,
                        borderBottomWidth: 2,
                        borderBottomColor: Colors.borders.light,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Text theme='h1' size={18}>Admin Portal</Text>
                    {onClose && (
                        <TouchableOpacity onPress={onClose}>
                            <Icons.CloseIcon size={24} color={Colors.text.h1} />
                        </TouchableOpacity>
                    )}
                </View>

                {/* Search */}
                <View transparent style={{ padding: 12 }}>
                    <SearchBox
                        placeholder="Search leagues..."
                        onChange={setSearchText}
                        hide_search_button
                    />
                </View>

                {/* Leagues Navigation */}
                <ScrollView style={{ flex: 1 }}>
                    <View transparent style={{ padding: 12, paddingTop: 4 }}>
                        {filteredLeagues.map(renderLeagueNav)}
                    </View>

                    {/* Divider */}
                    <View
                        transparent
                        style={{
                            height: 2,
                            backgroundColor: Colors.borders.light,
                            marginVertical: 12,
                        }}
                    />

                    {/* Global Tools */}
                    <View transparent style={{ padding: 12 }}>
                        <Text theme='description' size={11} style={{ marginBottom: 8, paddingLeft: 4 }}>
                            GLOBAL TOOLS
                        </Text>

                        <TouchableOpacity
                            onPress={() => selectTool('jobs')}
                            style={{
                                padding: 10,
                                paddingLeft: 16,
                                backgroundColor: selectedTool === 'jobs' ? Colors.views.header : 'transparent',
                                borderRadius: 4,
                                marginBottom: 2,
                            }}
                        >
                            <Text
                                theme={selectedTool === 'jobs' ? 'h2' : 'description'}
                                size={13}
                            >
                                Job Manager
                            </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            onPress={() => selectTool('auto-link')}
                            style={{
                                padding: 10,
                                paddingLeft: 16,
                                backgroundColor: selectedTool === 'auto-link' ? Colors.views.header : 'transparent',
                                borderRadius: 4,
                                marginBottom: 2,
                            }}
                        >
                            <Text
                                theme={selectedTool === 'auto-link' ? 'h2' : 'description'}
                                size={13}
                            >
                                Auto-Link
                            </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            onPress={() => selectTool('auto-fill')}
                            style={{
                                padding: 10,
                                paddingLeft: 16,
                                backgroundColor: selectedTool === 'auto-fill' ? Colors.views.header : 'transparent',
                                borderRadius: 4,
                                marginBottom: 2,
                            }}
                        >
                            <Text
                                theme={selectedTool === 'auto-fill' ? 'h2' : 'description'}
                                size={13}
                            >
                                Auto-Fill Manager
                            </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            onPress={() => selectTool('config')}
                            style={{
                                padding: 10,
                                paddingLeft: 16,
                                backgroundColor: selectedTool === 'config' ? Colors.views.header : 'transparent',
                                borderRadius: 4,
                                marginBottom: 2,
                            }}
                        >
                            <Text
                                theme={selectedTool === 'config' ? 'h2' : 'description'}
                                size={13}
                            >
                                Config Manager
                            </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            onPress={() => selectTool('router-markets')}
                            style={{
                                padding: 10,
                                paddingLeft: 16,
                                backgroundColor: selectedTool === 'router-markets' ? Colors.views.header : 'transparent',
                                borderRadius: 4,
                                marginBottom: 16,
                            }}
                        >
                            <Text
                                theme={selectedTool === 'router-markets' ? 'h2' : 'description'}
                                size={13}
                            >
                                Router Markets
                            </Text>
                        </TouchableOpacity>

                        {/* Reconcile Opportunities Button */}
                        <View transparent style={{ paddingLeft: 16, paddingRight: 16 }}>
                            <Button
                                title={reconcilingOpportunities ? "Reconciling..." : "Reconcile Opportunities"}
                                onPress={handleReconcileOpportunities}
                                disabled={reconcilingOpportunities}
                            />
                        </View>
                    </View>
                </ScrollView>
            </View>

            {/* Main Content Area */}
            <View transparent style={{ flex: 1 }}>
                <ScrollView style={{ flex: 1 }}>
                    <View transparent style={{ padding: 20 }}>
                        {/* Render content based on selection */}
                        {selectedTool === 'jobs' && (
                            <JobManager />
                        )}

                        {selectedTool === 'auto-link' && (
                            <View transparent>
                                <Text theme='h1' size={24}>Auto-Link</Text>
                                <Text theme='description' size={14} style={{ marginTop: 8 }}>
                                    Automatic linking tools coming soon...
                                </Text>
                            </View>
                        )}

                        {selectedTool === 'auto-fill' && (
                            <AutoFillView />
                        )}

                        {selectedTool === 'config' && (
                            <ConfigManager />
                        )}

                        {selectedTool === 'router-markets' && (
                            <RouterMarkets />
                        )}

                        {!selectedTool && selectedLeague && selectedSection === 'info' && (
                            <LeagueInfo league={selectedLeague} />
                        )}

                        {!selectedTool && selectedLeague && selectedSection === 'contests' && (
                            <LeagueContests league={selectedLeague} initialContestId={initialContestId} />
                        )}

                        {!selectedTool && selectedLeague && selectedSection === 'participants' && (
                            <LeagueParticipants league={selectedLeague} />
                        )}

                        {!selectedTool && !selectedLeague && (
                            <View transparent style={{ alignItems: 'center', paddingTop: 60 }}>
                                <Icons.AlertIcon size={64} color={Colors.text.descriptionLight} />
                                <Text theme='description' size={16} style={{ marginTop: 16 }}>
                                    Select a league to get started
                                </Text>
                            </View>
                        )}
                    </View>
                </ScrollView>
            </View>
        </View>
    );
};

export default DesktopAdminLayout;
